import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import supportContacts from '../../config/support';
import './FloatingHelpButton.css';

// Routes where the help button should NOT appear: login, admin, and the
// public (no-auth) preview pages. Everything else is an experiment step.
const HIDDEN_PREFIXES = ['/admin', '/public'];

// Human-readable label for the current step, used only to give the study team
// context in the prefilled message (no PII).
const STEP_LABELS = {
  '/dashboard': 'Dashboard',
  '/consent': 'Consent',
  '/pre-experiment': 'Pre-questionnaire',
  '/documentation-intro': 'Documentation intro',
  '/platform-tutorial': 'Platform tutorial',
  '/documentation-review': 'Documentation review',
  '/document-view': 'Design document viewer',
  '/experiment': 'Experiment entry',
  '/website1': 'Pentest',
  '/threatmodelviewer': 'Threat model viewer',
  '/post-experiment': 'Post-questionnaire',
  '/follow-up': 'Follow-up',
  '/findings-summary': 'Findings summary',
  '/thank-you': 'Thank you',
};

function stepLabelFor(pathname) {
  const key = Object.keys(STEP_LABELS).find(p => pathname === p || pathname.startsWith(p + '/'));
  return key ? STEP_LABELS[key] : 'Experiment';
}

// Pseudonymous participant id from the JWT (`sub`), so the study team can locate
// the participant without the message carrying their name/email.
function participantId() {
  try {
    const t = localStorage.getItem('access_token');
    if (!t) return null;
    return JSON.parse(atob(t.split('.')[1])).sub || null;
  } catch {
    return null;
  }
}

export default function FloatingHelpButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close the menu on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const telegram = (supportContacts.telegram || '').trim().replace(/^@/, '');
  const whatsapp = (supportContacts.whatsapp || '').replace(/[^0-9]/g, '');
  const email = (supportContacts.email || '').trim();

  // Hide on login / admin / public pages.
  if (HIDDEN_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p))) return null;
  if (location.pathname === '/') return null;
  // Nothing configured → render nothing.
  if (!telegram && !whatsapp && !email) return null;

  const pid = participantId();
  const context = `${pid ? `Participant ID: ${pid}` : 'Participant'} · page: ${stepLabelFor(location.pathname)}`;
  const message = `Hi, I need help with the DocuPen study. (${context})`;
  const enc = encodeURIComponent(message);

  const links = [];
  // t.me does not reliably prefill text for user chats, so it just opens the chat.
  if (telegram) links.push({ key: 'tg', icon: '✈️', label: 'Telegram', href: `https://t.me/${telegram}` });
  if (whatsapp) links.push({ key: 'wa', icon: '🟢', label: 'WhatsApp', href: `https://wa.me/${whatsapp}?text=${enc}` });
  if (email) links.push({ key: 'em', icon: '✉️', label: 'Email', href: `mailto:${email}?subject=${encodeURIComponent('DocuPen study — help request')}&body=${enc}` });

  return (
    <div className="floating-help" ref={rootRef}>
      {open && (
        <div className="floating-help-menu" role="menu" aria-label="Contact the study team">
          <div className="floating-help-note">Need help during the study? Reach the study team:</div>
          {links.map(l => (
            <a
              key={l.key}
              className="floating-help-link"
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <span className="fhl-icon" aria-hidden="true">{l.icon}</span>
              {l.label}
            </a>
          ))}
        </div>
      )}
      <button
        type="button"
        className="floating-help-toggle"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(o => !o)}
      >
        <span aria-hidden="true">💬</span>
        Need help?
      </button>
    </div>
  );
}
